/**
 * Sprint 211_FINAL — Parent Login (Email + 6-digit TOTP only).
 *
 * No password input. Two phases on one page:
 *   phase 'email'  — Email field → moves to TOTP
 *   phase 'totp'   — 6-digit code → validates → stores JWT → /parent-dashboard
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Mail, Loader2, Check, ScanLine } from 'lucide-react';
import { apiClient } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';

const ParentLoginTotp = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [phase, setPhase] = useState('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const next = (e) => { e?.preventDefault(); if (!email) return; setError(null); setPhase('totp'); };

  const verify = async () => {
    setError(null);
    if (code.length !== 6) { setError('Enter the 6-digit code from your authenticator app.'); return; }
    setBusy(true);
    try {
      const data = await apiClient.requestPublic('/api/auth/parent/totp-verify', {
        method: 'POST', body: JSON.stringify({ email: email.toLowerCase().trim(), code }),
      });
      if (data?.token) {
        apiClient.setToken(data.token, true);
      }
      // Hydrate AuthContext synchronously BEFORE navigating so ProtectedRoute
      // sees user!=null on the very first render of /parent-dashboard.
      // Mirrors the proven AuthContext.login() pattern: setToken → fetch /me
      // → setUser → cache → navigate.
      try {
        const fullUser = await apiClient.getCurrentUser();
        setUser(fullUser);
        try { localStorage.setItem('hibff_user_cache', JSON.stringify(fullUser)); } catch {}
      } catch (meErr) {
        // /api/users/me failed — token is bad, surface error and stay put
        setError('Verified, but could not load profile. Try again.');
        setBusy(false);
        return;
      }
      navigate('/parent-dashboard', { replace: true });
    } catch (e) {
      const msg = e?.message || '';
      if (msg.includes('totp_setup_not_complete')) {
        setError('You haven\'t finished pairing your authenticator. Re-register or contact support.');
      } else if (msg.includes('parent_not_found')) {
        setError('No parent profile found for that email.');
      } else if (msg.includes('invalid_totp')) {
        setError('Code didn\'t match. Try the next one your app shows.');
      } else {
        setError(msg || 'Could not verify.');
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md" data-testid="parent-login-totp">
        <div className="text-center mb-6">
          <div className="w-14 h-14 mx-auto mb-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center">
            <Shield size={28} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Parent sign-in</h1>
          <p className="text-sm text-gray-500 mt-1">Email + 6-digit code from your authenticator app. No password needed.</p>
        </div>

        {phase === 'email' && (
          <>
          <form onSubmit={next} className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 space-y-5" data-testid="parent-login-email-form">
            <label className="block">
              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Email</span>
              <div className="mt-1 relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} autoFocus required
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-100 outline-none"
                  placeholder="you@email.com" data-testid="parent-login-email-input" />
              </div>
            </label>
            <button type="submit" className="w-full py-3.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg" data-testid="parent-login-email-next">
              Continue →
            </button>
            <p className="text-xs text-gray-400 text-center">
              Don&apos;t have an account yet?{' '}
              <button type="button" onClick={() => navigate('/parent-register')} className="text-purple-600 font-semibold hover:underline">Register here</button>
            </p>
          </form>

          {/* First time here? — how the QR + 6-digit sign-in works */}
          <div className="mt-5 bg-indigo-50 border border-indigo-200 rounded-2xl p-5" data-testid="parent-login-how-it-works">
            <p className="text-sm font-bold text-indigo-900 mb-2.5">First time here? How parent sign-in works:</p>
            <ol className="space-y-2 text-sm text-indigo-900/90">
              <li className="flex gap-2.5"><span className="flex-shrink-0 w-5 h-5 rounded-full bg-indigo-600 text-white text-[11px] font-bold flex items-center justify-center">1</span><span><strong>Register once</strong> — name, email, phone and your typed signature.</span></li>
              <li className="flex gap-2.5"><span className="flex-shrink-0 w-5 h-5 rounded-full bg-indigo-600 text-white text-[11px] font-bold flex items-center justify-center">2</span><span><strong>Scan the QR code</strong> we show you with Google Authenticator, Authy or 1Password on your phone — a one-time step.</span></li>
              <li className="flex gap-2.5"><span className="flex-shrink-0 w-5 h-5 rounded-full bg-indigo-600 text-white text-[11px] font-bold flex items-center justify-center">3</span><span><strong>Sign in any time</strong> with your email + the 6-digit code your authenticator app shows. No password to remember or lose.</span></li>
            </ol>
            <button type="button" onClick={() => navigate('/parent-register')}
              className="mt-4 w-full py-2.5 rounded-xl bg-white border-2 border-indigo-300 text-indigo-700 font-semibold text-sm hover:bg-indigo-100"
              data-testid="parent-login-register-cta">
              Register &amp; scan your QR code →
            </button>
          </div>
          </>
        )}

        {phase === 'totp' && (
          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8" data-testid="parent-login-totp-form">
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
              <ScanLine size={16} className="text-emerald-600" /> Signed in as <strong className="text-gray-800">{email}</strong>
              <button onClick={() => setPhase('email')} className="ml-auto text-xs text-purple-600 hover:underline">Change</button>
            </div>
            <label className="block mb-1">
              <span className="text-[11px] font-bold text-gray-600 uppercase tracking-wide">6-digit code</span>
              <input type="text" inputMode="numeric" pattern="[0-9]{6}" value={code} autoFocus
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                onKeyDown={e => e.key === 'Enter' && verify()}
                placeholder="123 456"
                className="mt-1 w-full px-4 py-3 border-2 border-emerald-300 rounded-xl focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none text-center text-3xl tracking-[0.4em] font-mono"
                data-testid="parent-login-totp-code" />
            </label>
            {error && <p className="text-sm text-red-600 mt-2" data-testid="parent-login-totp-error">{error}</p>}
            <button onClick={verify} disabled={busy || code.length !== 6}
              className="mt-4 w-full py-3 bg-gradient-to-r from-emerald-500 to-green-600 text-white font-semibold rounded-xl hover:shadow-lg disabled:opacity-50 inline-flex items-center justify-center gap-2"
              data-testid="parent-login-totp-verify">
              {busy ? <Loader2 size={18} className="animate-spin" /> : <><Check size={16} /> Verify &amp; Enter Dashboard</>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ParentLoginTotp;
